/**
 *
 * MultiSelectComponent
 *
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { createFilterOptions, useAutocomplete } from "@material-ui/lab";
import {
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import { KeyboardArrowDown } from "@material-ui/icons";
import Delete from "images/DeleteIcon";
import * as PropTypes from "prop-types";

const useLabelStyles = makeStyles({
  root: {
    "&$focused": {
      color: "rgb(159,159,159)",

      "& ~ fieldset": {
        borderColor: "red",
      },
    },
  },
  focused: {},
});

const useInputStyles = makeStyles(theme => ({
  root: {
    cursor: "pointer",
    overflowWrap: "break-word",
    "&$focused $notchedOutline": {
      borderColor: "rgba(0,0,0,0.87)",
      borderWidth: "1px",
    },
    "&.active $notchedOutline": {
      borderColor: "rgba(0,0,0,0.87)",
      borderWidth: "1px",
      borderRadius: "15px 15px 0 0",
    },
  },
  notchedOutline: {
    borderRadius: "50px",
    transition: "border-radius 150ms ease-in-out",
  },
  focused: {},
}));

const useStyles = makeStyles(theme => ({
  root: {
    pointerEvents: ({ disabled }) => (disabled ? "none" : "initial"),
    position: "relative",
    wordWrap: "wrap",

    "&$focused fieldset": {
      borderColor: "rgba(0,0,0,0.50)",
      borderWidth: "1px",
    },
  },

  focused: {},
  notchedOutline: {},
  iconButton: {
    padding: "5px",
  },
  label: {
    display: "block",
  },
  input: {
    borderRadius: "50px",
    transition: "border-radius 150ms ease-in-out",

    "&.active $notchedOutline": {
      borderColor: "rgba(0,0,0,0.50)",
      borderWidth: "1px",
      borderRadius: "15px 15px 0 0",
    },
  },
  tagContainer: {
    display: "block",
  },
  disabledPointer: {
    pointerEvents: "none",
  },
  listbox: {
    width: "100%",
    margin: 0,
    padding: 0,
    zIndex: 10,
    position: "absolute",
    listStyle: "none",
    border: "1px solid rgba(0, 0, 0, 0.87)",
    borderTop: "none",
    borderRadius: "0 0 15px 15px",
    backgroundColor: "#FFF",
    overflow: "auto",
    maxHeight: 200,
    '& li[data-focus="true"]': {
      backgroundColor: "rgba(0,0,0,0.10)",
      color: "black",
      cursor: "pointer",
    },
  },
  specialOption: {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
    alignItems: "center",
    height: "35px",

    "& > img": {
      borderRadius: "50%",
      height: "30px",
      width: "30px",
      objectFit: "cover",
    },
  },
  dropdownIcon: {
    transition: "transform 150ms ease-in-out",
    pointerEvents: "none",

    "&.active": {
      transform: "rotate(180deg)",
    },

    "&.active.withPointerEvents": {
      transform: "rotate(0)",
    },

    "&.withPointerEvents": {
      pointerEvents: "initial",
    },
  },
}));

function SingleSelectComponent({
  label,
  value = {},
  onChange = {},
  options = [],
  labelProperty = "label",
  className = "",
  onClear,
  noIcon = false,
  hasError = false,
  canCreate = false,
  additionalAutocompleteOptions = {},
  disabled = false,
}) {
  const classes = useStyles({ disabled });
  const inputLabel = useRef();
  const inputStyles = useInputStyles();
  const labelClasses = useLabelStyles();
  const [labelWidth, setLabelWidth] = useState(0);

  useEffect(() => {
    setLabelWidth(inputLabel.current.offsetWidth);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getOptions = useMemo(() => options.slice(), [options]);

  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    setAnchorEl,
    focused,
    ...rest
  } = useAutocomplete({
    options: getOptions,
    disableCloseOnSelect: false,
    multiple: false,
    filterOptions: (o, params) => {
      const filtered = createFilterOptions()(o, params);

      if (canCreate && params.inputValue !== "" && filtered.length === 0) {
        filtered.push({
          value: params.inputValue,
          isNew: true,
          label: `Add "${params.inputValue}"`,
        });
      }

      return filtered;
    },
    // blurOnSelect: true,
    value,
    onChange: (e, newValue) => {
      if (newValue) {
        onChange(newValue);
      } else {
        onChange({});
      }
    },
    // Material-UI: The value provided to useAutocomplete is invalid.
    // None of the options match with `{}`.
    // You can use the `getOptionSelected` prop to customize the equality test.
    getOptionSelected: (option, valueToEval) => valueToEval === value,
    getOptionLabel: option => option[labelProperty] || "",
    ...additionalAutocompleteOptions,
  });

  const hasValue =
    rest.value && rest.value.label && rest.value.label.length > 0;

  return (
    <div {...getRootProps()} className={clsx(classes.root, className)}>
      <FormControl
        aria-disabled={disabled}
        disabled={disabled}
        fullWidth
        variant="outlined"
        htmlFor={inputLabel}
        error={hasError}
      >
        <InputLabel
          classes={labelClasses}
          {...getInputLabelProps()}
          margin="dense"
          ref={inputLabel}
          disabled={disabled}
        >
          {label}
        </InputLabel>
        <OutlinedInput
          data-cy={label}
          fullWidth
          labelWidth={labelWidth}
          {...getInputProps()}
          disabled={disabled}
          inputRef={setAnchorEl}
          className={clsx({
            active: groupedOptions.length > 0,
          })}
          classes={inputStyles}
          margin="dense"
          {...(!focused && hasValue
            ? {
                value: rest.value.label,
              }
            : {})}
          endAdornment={
            <InputAdornment
              disablePointerEvents={!(hasValue && onClear && !noIcon)}
              disabled={disabled}
              className={clsx(classes.dropdownIcon, {
                active: groupedOptions.length > 0,
                withPointerEvents: hasValue,
              })}
              position="end"
            >
              {hasValue && onClear && !noIcon ? (
                <IconButton size="small" onClick={onClear} data-cy="deleteIcon">
                  <Delete className={classes.iconButton} />
                </IconButton>
              ) : (
                <KeyboardArrowDown className={classes.disabledPointer} />
              )}
            </InputAdornment>
          }
        />
      </FormControl>
      {groupedOptions.length > 0 ? (
        <ul
          className={classes.listbox}
          {...getListboxProps()}
          data-cy="dropDownList"
        >
          {groupedOptions.map((option, index) => {
            const optionProps = getOptionProps({ option, index });

            return (
              <MenuItem {...optionProps}>
                {option.url || option.svg ? (
                  <div className={classes.specialOption}>
                    <span>{option.label}</span>
                    {option.url ? <img src={option.url} /> : option.svg}
                  </div>
                ) : (
                  option.label
                )}
              </MenuItem>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

SingleSelectComponent.propTypes = {
  label: PropTypes.any,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  options: PropTypes.array,
};

export default SingleSelectComponent;
